import type { Segno } from '../types'

export const PAUSE_MS = 400
export const CONTIGUOUS_TOLERANCE_SECONDS = 0.15

export interface PlaybackRange {
  startTime: number
  endTime: number
}

export function roundTenth(value: number): number {
  return Math.max(0, Math.round((value + Number.EPSILON) * 10) / 10)
}

export function nudge(value: number, delta: -0.1 | 0.1): number {
  return roundTenth(value + delta)
}

export function selectedQueue(segnos: Segno[], selected: Set<number>): Segno[] {
  return segnos
    .filter((segno) => selected.has(segno.order))
    .slice()
    .sort((a, b) => a.startTime - b.startTime || a.order - b.order)
}

/**
 * 여러 세뇨를 연속 재생할 때 선택된 구간들을 이어 붙이지 않고,
 * 가장 이른 시작부터 가장 늦은 끝까지를 하나의 재생 구간으로 만듭니다.
 */
export function continuousSelectedRange(segnos: Segno[], selected: Set<number>): PlaybackRange | null {
  const queue = selectedQueue(segnos, selected)
  if (queue.length < 2) return null

  return {
    startTime: Math.min(...queue.map((segno) => segno.startTime)),
    endTime: Math.max(...queue.map((segno) => segno.endTime)),
  }
}

/** 현재 재생 시각에 실제로 걸쳐 있는 세뇨를 찾습니다. 겹치는 경우 더 늦게 시작한 세뇨를 우선합니다. */
export function segnoAtTime(segnos: Segno[], currentTime: number): Segno | null {
  return segnos
    .filter((segno) => currentTime >= segno.startTime - 0.03 && currentTime < segno.endTime - 0.03)
    .slice()
    .sort((left, right) => right.startTime - left.startTime || left.order - right.order)[0] ?? null
}

export function isContiguous(current: Segno, next: Segno): boolean {
  return Math.abs(next.startTime - current.endTime) <= CONTIGUOUS_TOLERANCE_SECONDS
}
